import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet } from "react-native";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import { StackScreenProps } from "@react-navigation/stack";
import Text from "../../components/Text";
import SelectRoleImage from "../../assets/images/select-role-image.svg";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { CompositeScreenProps, useFocusEffect } from "@react-navigation/core";
import { useQuery } from "@apollo/client/react";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { ONBOARDING_INVITATIONS } from "../../graphql/invitation";
import { useAtomValue } from "jotai";
import { invitationCodeStepDone, signupInvitationIdsToIgnore } from "../../atoms/auth";
import Loading from "../../components/Loading";

type SelectRoleScreenProps = CompositeScreenProps<
    StackScreenProps<SignupSigninStackParamList, "SelectRole">,
    StackScreenProps<RootStackParamList>
>;

const SelectRoleScreen = ({ navigation, route }: SelectRoleScreenProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { isSignup = false } = route.params || {};
    const { data, loading: invitationsLoading } = useQuery(ONBOARDING_INVITATIONS, {
        errorPolicy: "ignore",
        fetchPolicy: "network-only",
    });
    const invitationsToIgnore = useAtomValue(signupInvitationIdsToIgnore);
    const invitationsList = useMemo(
        () =>
            (data?.onboardingInvitationsReceived || []).filter(
                (i) => !invitationsToIgnore.includes(i.id),
            ),
        [data?.onboardingInvitationsReceived, invitationsToIgnore],
    );

    const goToClientInfo = useCallback(() => {
        navigation.navigate("CheckClientInfo");
    }, [navigation]);

    const goToPrivateClientInfo = useCallback(() => {
        navigation.navigate("CheckPrivateClientInfo");
    }, [navigation]);

    const goToWorkerInfo = useCallback(() => {
        navigation.navigate("CheckWorkerInfo");
    }, [navigation]);

    const hasInvitation = useMemo(() => invitationsList.length > 0, [invitationsList.length]);

    // With no invitation sent to the phone number, the user may have one sent by email: ask for its
    // code first, once (coming back from the code step stays here).
    const codeStepDone = useAtomValue(invitationCodeStepDone);
    const [codeStepAsked, setCodeStepAsked] = useState(false);
    const needsCodeStep = !invitationsLoading && !hasInvitation && !codeStepDone && !codeStepAsked;

    useFocusEffect(
        useCallback(() => {
            if (hasInvitation) {
                navigation.navigate("Invitation", { invitationId: invitationsList[0].id });
            } else if (needsCodeStep) {
                setCodeStepAsked(true);
                navigation.navigate("InvitationCode");
            }
        }, [hasInvitation, invitationsList, navigation, needsCodeStep]),
    );

    const loading = invitationsLoading || hasInvitation || needsCodeStep;

    return (
        <ScreenCard
            headband={
                !isSignup
                    ? { mode: "warning", text: i18n.t("screens.auth.select_role.no_account") }
                    : undefined
            }
            buttons={
                !loading
                    ? [
                          {
                              title: i18n.t("screens.auth.select_role.client"),
                              onPress: goToClientInfo,
                              mode: "primary",
                          },
                          {
                              title: i18n.t("screens.auth.select_role.private_client"),
                              onPress: goToPrivateClientInfo,
                              mode: "tertiary",
                          },
                          {
                              title: i18n.t("screens.auth.select_role.worker"),
                              onPress: goToWorkerInfo,
                              mode: "secondary",
                          },
                      ]
                    : undefined
            }
            isContentContainerFlex
        >
            {!loading ? (
                <VStack gap={2}>
                    <SelectRoleImage width="100%" />
                    <Text style={styles.subTitle}>
                        {i18n.t("screens.auth.select_role.description")}
                    </Text>
                </VStack>
            ) : (
                <Loading />
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                subTitle: {
                    ...fonts.titleMedium,
                    marginHorizontal: 18,
                    textAlign: "center",
                },
            }),
        [fonts.titleMedium],
    );
};

export default SelectRoleScreen;
